/* eslint-disable camelcase */
import { ApolloError, gql, useMutation } from '@apollo/client';

import { useNotifications } from 'contexts/notifications/use-notifications';
import { useGetMe } from 'hooks/use-get-me';
import { useProducts } from 'hooks/use-products';
import Amplitude from 'services/Amplitude';

const JOIN_PRODUCT_MUTATION = gql`
  mutation JoinProduct($product: String!) {
    JoinProduct(product: $product)
  }
`;

type Props = {
  organization_id: string;
  product: string;
};

type UseJoinProduct = {
  handleJoin(token: string): void;
  isLoading: boolean;
};

export const useJoinProduct = ({ organization_id, product }: Props): UseJoinProduct => {
  const { showToast } = useNotifications();
  const { email } = useGetMe();
  const { handleRedirectToProduct, isFetchingProducts } = useProducts();

  const handleError = (error: ApolloError): void => {
    let err;

    try {
      const errorDescription = error.graphQLErrors[0]?.extensions?.description as string;
      const stringfiedError = errorDescription?.split(' - ')[1];

      err = stringfiedError ? JSON.parse(stringfiedError) : null;
    } catch (e) {
      console.error(e);
    }

    showToast({
      content: err?.error?.message ?? 'Joining to a selected product failed',
      variant: 'error',
    });
  };

  const handleSuccess = (): void => {
    showToast({
      content: 'Joined product successfully',
      variant: 'success',
    });
    handleRedirectToProduct(product, organization_id);
  };

  const [joinProduct, { loading: isJoiningProduct }] = useMutation<boolean>(JOIN_PRODUCT_MUTATION, {
    onError: handleError,
    onCompleted: handleSuccess,
  });

  const handleJoin = (token: string): void => {
    joinProduct({
      variables: { product },
      context: { headers: { Authorization: `Bearer ${token}` } },
    });
    Amplitude.logEvent(email, 'JOIN_PRODUCT_CLICK', { product });
  };

  return {
    handleJoin,
    isLoading: isFetchingProducts || isJoiningProduct,
  };
};
